import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { PARClientPanel } from "@/components/admin/PARClientPanel";
import { CompanyManagementCard } from "@/components/admin/CompanyManagementCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useRole } from "@/hooks/useRole";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import {
  ArrowLeft,
  RefreshCw,
  Save,
  Trash2,
  Plus,
  Shield,
  Flag,
  Building2,
  Settings2,
  FileText,
  BarChart3,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Invoice,
  Settings,
  InvoiceStatus,
  getInvoiceStatusLabel,
  getInvoiceStatusColor,
} from "@/types";

export default function AdminPage() {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [settings, setSettings] = useState<Settings | null>(null);
  const [editingInvoice, setEditingInvoice] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<Partial<Invoice>>({});
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isAdmin, isLoading: isLoadingRole } = useRole();
  const { flags, loading: flagsLoading, toggle: toggleFlag } = useFeatureFlags();

  const isDiscoRunEmail = user?.email?.endsWith("@trydiscorun.com") ?? false;
  const canAccess = isAdmin || isDiscoRunEmail;

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    const [{ data: inv }, { data: set }] = await Promise.all([
      supabase.from("invoices").select("*").order("created_at", { ascending: false }),
      supabase.from("settings").select("*").limit(1).single(),
    ]);
    setInvoices((inv as Invoice[]) || []);
    setSettings(set as Settings | null);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleEdit = (invoice: Invoice) => {
    setEditingInvoice(invoice.id);
    setEditValues({ ...invoice });
  };

  const VALID_TRANSITIONS: Record<InvoiceStatus, InvoiceStatus[]> = {
    [InvoiceStatus.PENDING]: [
      InvoiceStatus.EARLY_PAYMENT_SCHEDULED,
      InvoiceStatus.STANDARD_PAYMENT,
    ],
    [InvoiceStatus.EARLY_PAYMENT_SCHEDULED]: [InvoiceStatus.EARLY_PAYMENT_COMPLETED],
    [InvoiceStatus.EARLY_PAYMENT_COMPLETED]: [],
    [InvoiceStatus.STANDARD_PAYMENT]: [],
  };

  const handleSave = async (id: string) => {
    const amount = editValues.payment_amount;
    if (amount == null || amount <= 0) {
      toast({
        title: "Invalid amount",
        description: "Amount must be a positive number.",
        variant: "destructive",
      });
      return;
    }
    const apr = editValues.apr_rate;
    if (apr == null || apr < 0 || apr > 100) {
      toast({
        title: "Invalid APR",
        description: "APR/discount rate must be between 0 and 100.",
        variant: "destructive",
      });
      return;
    }
    const dueDate = editValues.payment_due_date;
    if (dueDate) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (new Date(dueDate) < today) {
        toast({
          title: "Invalid due date",
          description: "Due date must not be in the past.",
          variant: "destructive",
        });
        return;
      }
    }
    const originalInvoice = invoices.find((inv) => inv.id === id);
    const originalStatus = originalInvoice?.status as InvoiceStatus | undefined;
    const newStatus = editValues.status as InvoiceStatus | undefined;
    if (originalStatus && newStatus && originalStatus !== newStatus) {
      const allowed = VALID_TRANSITIONS[originalStatus] ?? [];
      if (!allowed.includes(newStatus)) {
        toast({
          title: "Invalid status transition",
          description: `Cannot transition from "${getInvoiceStatusLabel(originalStatus)}" to "${getInvoiceStatusLabel(newStatus)}".`,
          variant: "destructive",
        });
        return;
      }
    }
    const { error } = await supabase
      .from("invoices")
      .update({
        payment_amount: editValues.payment_amount,
        payment_due_date: editValues.payment_due_date,
        supplier_name: editValues.supplier_name,
        supplier_email: editValues.supplier_email,
        status: editValues.status as InvoiceStatus,
        apr_rate: editValues.apr_rate,
        discount_amount: editValues.discount_amount,
        discounted_payment_amount: editValues.discounted_payment_amount,
        email_sent_at: editValues.email_sent_at,
        email_error: editValues.email_error,
      })
      .eq("id", id);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Saved", description: "Invoice updated." });
      setEditingInvoice(null);
      fetchData();
    }
  };

  const handleDelete = async (id: string) => {
    const { error } = await supabase.from("invoices").delete().eq("id", id);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Deleted" });
      fetchData();
    }
  };

  const handleSettingsSave = async () => {
    if (!settings) return;
    const { error } = await supabase
      .from("settings")
      .update({
        early_payment_budget: settings.early_payment_budget,
        early_payment_days: settings.early_payment_days,
        buyer_name: settings.buyer_name,
        notification_email: settings.notification_email,
      })
      .eq("id", settings.id);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Settings saved" });
    }
  };

  const addTestInvoice = async () => {
    const suppliers = ["Test Supplier A", "Test Supplier B", "Test Supplier C"];
    const supplier = suppliers[Math.floor(Math.random() * suppliers.length)];
    const amount = 10000 + Math.floor(Math.random() * 40000);
    const dueDate = new Date();
    dueDate.setDate(dueDate.getDate() + 30);
    const apr = 12;
    const daysEarly = 25;
    const discountRate = (apr / 100) * (daysEarly / 365);
    const discountAmount = Math.round(amount * discountRate * 100) / 100;
    const { error } = await supabase.from("invoices").insert({
      payment_amount: amount,
      payment_due_date: dueDate.toISOString().split("T")[0],
      supplier_name: supplier,
      supplier_email: `${supplier.toLowerCase().replace(/\s+/g, "")}@test.com`,
      apr_rate: apr,
      discount_amount: discountAmount,
      discounted_payment_amount: Math.round((amount - discountAmount) * 100) / 100,
      status: InvoiceStatus.PENDING,
    });
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Test invoice added" });
      fetchData();
    }
  };

  if (isLoadingRole) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!canAccess) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-8 text-center">
          <Shield className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
          <h2 className="text-xl font-display font-bold text-foreground mb-2">Access Denied</h2>
          <p className="text-sm text-muted-foreground mb-6">
            Admin access is required to view this page.
          </p>
          <Button onClick={() => navigate("/")} variant="outline">
            Go to Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card/80 backdrop-blur-md sticky top-0 z-50">
        <div className="w-full px-6 py-3 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/")}>
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <h1 className="text-lg font-display font-bold">Admin Panel</h1>
          <div className="ml-auto flex gap-2">
            <Button variant="outline" size="sm" onClick={fetchData}>
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
            </Button>
            {import.meta.env.DEV && (
              <Button size="sm" onClick={addTestInvoice} variant="outline">
                <Plus className="w-3.5 h-3.5 mr-1" /> Add Test Invoice
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="w-full px-6 py-6">
        <Tabs defaultValue="companies" className="space-y-5">
          <TabsList className="bg-muted/60 p-1">
            <TabsTrigger value="companies" className="flex items-center gap-1.5 text-sm px-4">
              <Building2 className="w-3.5 h-3.5" />
              Companies
            </TabsTrigger>
            <TabsTrigger value="feature-flags" className="flex items-center gap-1.5 text-sm px-4">
              <Flag className="w-3.5 h-3.5" />
              Feature Flags
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex items-center gap-1.5 text-sm px-4">
              <Settings2 className="w-3.5 h-3.5" />
              Settings
            </TabsTrigger>
            <TabsTrigger value="invoices" className="flex items-center gap-1.5 text-sm px-4">
              <FileText className="w-3.5 h-3.5" />
              Invoices
            </TabsTrigger>
            <TabsTrigger value="analytics" className="flex items-center gap-1.5 text-sm px-4">
              <BarChart3 className="w-3.5 h-3.5" />
              Analytics
            </TabsTrigger>
          </TabsList>

          {/* Companies Tab */}
          <TabsContent value="companies" className="space-y-0">
            <CompanyManagementCard />
          </TabsContent>

          {/* Feature Flags Tab */}
          <TabsContent value="feature-flags">
            <Card className="p-5 space-y-4">
              <div>
                <h2 className="text-base font-semibold font-display">Feature Flags</h2>
                <p className="text-xs text-muted-foreground">
                  Enable or disable features for all users
                </p>
              </div>
              {flagsLoading ? (
                <div className="space-y-2">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-10 rounded-md bg-muted animate-pulse" />
                  ))}
                </div>
              ) : flags.length === 0 ? (
                <p className="text-sm text-muted-foreground">No feature flags configured.</p>
              ) : (
                <div className="divide-y divide-border">
                  {flags.map((flag) => (
                    <div key={flag.id} className="flex items-center justify-between py-3">
                      <div>
                        <p className="text-sm font-medium">
                          {flag.name
                            .split("_")
                            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                            .join(" ")}
                        </p>
                        <p className="text-xs text-muted-foreground font-mono">{flag.name}</p>
                      </div>
                      <Switch
                        checked={flag.enabled}
                        onCheckedChange={(checked) => toggleFlag(flag.id, checked)}
                      />
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            {settings ? (
              <Card className="p-5 space-y-4">
                <div>
                  <h2 className="text-base font-semibold font-display">Global Settings</h2>
                  <p className="text-xs text-muted-foreground">Default system-wide configuration</p>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground">Budget</label>
                    <Input
                      type="number"
                      value={settings.early_payment_budget}
                      onChange={(e) =>
                        setSettings({ ...settings, early_payment_budget: Number(e.target.value) })
                      }
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground">Payment Days</label>
                    <Input
                      type="number"
                      value={settings.early_payment_days}
                      onChange={(e) =>
                        setSettings({ ...settings, early_payment_days: Number(e.target.value) })
                      }
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground">Buyer Name</label>
                    <Input
                      value={settings.buyer_name}
                      onChange={(e) => setSettings({ ...settings, buyer_name: e.target.value })}
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground">Notification Email</label>
                    <Input
                      value={settings.notification_email}
                      onChange={(e) =>
                        setSettings({ ...settings, notification_email: e.target.value })
                      }
                    />
                  </div>
                </div>
                <Button size="sm" onClick={handleSettingsSave}>
                  <Save className="w-3.5 h-3.5 mr-1" /> Save Settings
                </Button>
              </Card>
            ) : (
              <Card className="p-8 text-center text-sm text-muted-foreground">
                {isLoading ? "Loading…" : "No settings found."}
              </Card>
            )}
          </TabsContent>

          {/* Invoices Tab */}
          <TabsContent value="invoices">
            <Card className="overflow-hidden">
              <div className="p-5 border-b border-border flex items-center justify-between">
                <div>
                  <h2 className="text-base font-semibold font-display">
                    Invoices ({invoices.length})
                  </h2>
                  <p className="text-xs text-muted-foreground">All invoices across the system</p>
                </div>
              </div>
              {isLoading ? (
                <div className="p-8 text-center text-muted-foreground">Loading...</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-muted/50 border-b border-border">
                        <th className="text-left p-2 font-medium text-muted-foreground">
                          Supplier
                        </th>
                        <th className="text-left p-2 font-medium text-muted-foreground">Amount</th>
                        <th className="text-left p-2 font-medium text-muted-foreground">
                          Due Date
                        </th>
                        <th className="text-left p-2 font-medium text-muted-foreground">Status</th>
                        <th className="text-left p-2 font-medium text-muted-foreground">APR</th>
                        <th className="text-left p-2 font-medium text-muted-foreground">
                          Discount
                        </th>
                        <th className="text-left p-2 font-medium text-muted-foreground">
                          Discounted Amt
                        </th>
                        <th className="text-left p-2 font-medium text-muted-foreground">
                          Email Error
                        </th>
                        <th className="text-right p-2 font-medium text-muted-foreground">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoices.map((inv) => {
                        const isEditing = editingInvoice === inv.id;
                        return (
                          <tr key={inv.id} className="border-b border-border hover:bg-muted/30">
                            <td className="p-2">
                              {isEditing ? (
                                <Input
                                  className="h-7 text-xs"
                                  value={editValues.supplier_name || ""}
                                  onChange={(e) =>
                                    setEditValues({ ...editValues, supplier_name: e.target.value })
                                  }
                                />
                              ) : (
                                <span className="font-medium">{inv.supplier_name}</span>
                              )}
                            </td>
                            <td className="p-2">
                              {isEditing ? (
                                <Input
                                  className="h-7 text-xs w-24"
                                  type="number"
                                  value={editValues.payment_amount || 0}
                                  onChange={(e) =>
                                    setEditValues({
                                      ...editValues,
                                      payment_amount: Number(e.target.value),
                                    })
                                  }
                                />
                              ) : (
                                `$${inv.payment_amount.toLocaleString()}`
                              )}
                            </td>
                            <td className="p-2">
                              {isEditing ? (
                                <Input
                                  className="h-7 text-xs"
                                  type="date"
                                  value={editValues.payment_due_date || ""}
                                  onChange={(e) =>
                                    setEditValues({
                                      ...editValues,
                                      payment_due_date: e.target.value,
                                    })
                                  }
                                />
                              ) : (
                                inv.payment_due_date
                              )}
                            </td>
                            <td className="p-2">
                              {isEditing ? (
                                <Select
                                  value={editValues.status || InvoiceStatus.PENDING}
                                  onValueChange={(v) => setEditValues({ ...editValues, status: v })}
                                >
                                  <SelectTrigger className="h-7 text-xs w-32">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value={InvoiceStatus.PENDING}>
                                      {getInvoiceStatusLabel(InvoiceStatus.PENDING)}
                                    </SelectItem>
                                    <SelectItem value={InvoiceStatus.EARLY_PAYMENT_SCHEDULED}>
                                      {getInvoiceStatusLabel(InvoiceStatus.EARLY_PAYMENT_SCHEDULED)}
                                    </SelectItem>
                                    <SelectItem value={InvoiceStatus.STANDARD_PAYMENT}>
                                      {getInvoiceStatusLabel(InvoiceStatus.STANDARD_PAYMENT)}
                                    </SelectItem>
                                    <SelectItem value={InvoiceStatus.EARLY_PAYMENT_COMPLETED}>
                                      {getInvoiceStatusLabel(InvoiceStatus.EARLY_PAYMENT_COMPLETED)}
                                    </SelectItem>
                                  </SelectContent>
                                </Select>
                              ) : (
                                <Badge
                                  className={`${getInvoiceStatusColor(inv.status)} text-[10px] px-1.5 py-0`}
                                >
                                  {getInvoiceStatusLabel(inv.status)}
                                </Badge>
                              )}
                            </td>
                            <td className="p-2">
                              {isEditing ? (
                                <Input
                                  className="h-7 text-xs w-16"
                                  type="number"
                                  value={editValues.apr_rate || 0}
                                  onChange={(e) =>
                                    setEditValues({
                                      ...editValues,
                                      apr_rate: Number(e.target.value),
                                    })
                                  }
                                />
                              ) : (
                                `${inv.apr_rate}%`
                              )}
                            </td>
                            <td className="p-2">
                              {isEditing ? (
                                <Input
                                  className="h-7 text-xs w-20"
                                  type="number"
                                  value={editValues.discount_amount || 0}
                                  onChange={(e) =>
                                    setEditValues({
                                      ...editValues,
                                      discount_amount: Number(e.target.value),
                                    })
                                  }
                                />
                              ) : inv.discount_amount ? (
                                `$${inv.discount_amount.toLocaleString()}`
                              ) : (
                                "—"
                              )}
                            </td>
                            <td className="p-2">
                              {isEditing ? (
                                <Input
                                  className="h-7 text-xs w-24"
                                  type="number"
                                  value={editValues.discounted_payment_amount || 0}
                                  onChange={(e) =>
                                    setEditValues({
                                      ...editValues,
                                      discounted_payment_amount: Number(e.target.value),
                                    })
                                  }
                                />
                              ) : inv.discounted_payment_amount ? (
                                `$${inv.discounted_payment_amount.toLocaleString()}`
                              ) : (
                                "—"
                              )}
                            </td>
                            <td className="p-2 max-w-32 truncate text-destructive">
                              {isEditing ? (
                                <Input
                                  className="h-7 text-xs"
                                  value={editValues.email_error || ""}
                                  onChange={(e) =>
                                    setEditValues({
                                      ...editValues,
                                      email_error: e.target.value || null,
                                    })
                                  }
                                />
                              ) : (
                                inv.email_error || "—"
                              )}
                            </td>
                            <td className="p-2 text-right">
                              {isEditing ? (
                                <div className="flex gap-1 justify-end">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-6 text-[10px] px-2"
                                    onClick={() => setEditingInvoice(null)}
                                  >
                                    Cancel
                                  </Button>
                                  <Button
                                    size="sm"
                                    className="h-6 text-[10px] px-2"
                                    onClick={() => handleSave(inv.id)}
                                  >
                                    Save
                                  </Button>
                                </div>
                              ) : (
                                <div className="flex gap-1 justify-end">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-6 text-[10px] px-2"
                                    onClick={() => handleEdit(inv)}
                                  >
                                    Edit
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                    className="h-6 text-[10px] px-2"
                                    onClick={() => handleDelete(inv.id)}
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <PARClientPanel />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
